import React, { useState, useEffect, useMemo } from 'react';
import {
    Button,
    Tab,
    Tabs,
    TextField,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Checkbox,
    CircularProgress,
    Chip,
    Box,
    Badge,
    Typography,
    IconButton,
    Tooltip,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { getProductsList, getUrlPathsList } from '../../api/analytics3d';
import { AppDialog } from '../ui';

interface Item {
    id: number;
    name: string;
}

export interface AppliedFilters {
    productIds: number[] | null;
    urlPathIds: number[] | null;
    eventId: string | null;
    tempEventId: string | null;
    customerId: string | null;
    template: string | null;
}

interface Props {
    queryParams: any;
    onApplyFilter: (filters: AppliedFilters) => void;
    loading?: boolean;
}

type TextFilterKey = 'eventId' | 'tempEventId' | 'customerId' | 'template';

const TEXT_FILTERS: { key: TextFilterKey; label: string; placeholder: string }[] = [
    { key: 'eventId', label: 'Event ID', placeholder: 'Enter event ID…' },
    { key: 'tempEventId', label: 'Temp Event ID', placeholder: 'Enter temp event ID…' },
    { key: 'customerId', label: 'Customer ID', placeholder: 'Enter customer ID…' },
    { key: 'template', label: 'Template', placeholder: 'Enter template…' },
];

// ─── Display name ────────────────────────────────────────────────────────────

const getDisplayName = (item: Item): string =>
    item.name === '/' ? 'Home Page (/)' : item.name;

// ─── Favorites persistence (localStorage, lives forever) ─────────────────────

const LS_KEY = {
    url: 'mockup_filter_favorites_url_v1',
    product: 'mockup_filter_favorites_product_v1',
} as const;

// Text-filter values (Event ID / Customer ID / Template): history of searches
// that returned results, plus starred favorites — both persisted forever
const TEXT_LS_KEY = {
    favorites: 'mockup_filter_text_favorites_v1',
    history: 'mockup_filter_text_history_v1',
} as const;

type TextValuesMap = Record<TextFilterKey, string[]>;

const emptyTextValuesMap = (): TextValuesMap => ({ eventId: [], tempEventId: [], customerId: [], template: [] });

const loadTextValues = (kind: keyof typeof TEXT_LS_KEY): TextValuesMap => {
    try {
        const raw = localStorage.getItem(TEXT_LS_KEY[kind]);
        const parsed = raw ? JSON.parse(raw) : null;
        return { ...emptyTextValuesMap(), ...(parsed || {}) };
    } catch {
        return emptyTextValuesMap();
    }
};

const persistTextValues = (kind: keyof typeof TEXT_LS_KEY, map: TextValuesMap) => {
    try {
        localStorage.setItem(TEXT_LS_KEY[kind], JSON.stringify(map));
    } catch (e) {
        console.warn('Could not save text filter values to localStorage', e);
    }
};

const TEXT_HISTORY_LIMIT = 20;

const loadFavorites = (type: 'url' | 'product'): Item[] => {
    try {
        const raw = localStorage.getItem(LS_KEY[type]);
        return raw ? (JSON.parse(raw) as Item[]) : [];
    } catch {
        return [];
    }
};

const persistFavorites = (type: 'url' | 'product', items: Item[]) => {
    try {
        localStorage.setItem(LS_KEY[type], JSON.stringify(items));
    } catch (e) {
        console.warn('Could not save favorites to localStorage', e);
    }
};

// ─── Search scoring ───────────────────────────────────────────────────────────

/**
 * Word-based relevance scoring. Splits both text and query into words,
 * awards points for exact word matches, prefix matches, and substring matches.
 * Higher score = more relevant.
 */
const scoreSearch = (text: string, query: string): number => {
    const q = query.toLowerCase().trim();
    if (!q) return 1;

    const queryWords = q.split(/\s+/).filter(Boolean);
    const textWords = text.toLowerCase().split(/[\s/\-_"'(),]+/).filter(Boolean);

    let totalScore = 0;
    for (const qWord of queryWords) {
        let wordScore = 0;
        for (const tWord of textWords) {
            if (tWord === qWord) wordScore += 3;
            else if (tWord.startsWith(qWord)) wordScore += 2;
            else if (tWord.includes(qWord)) wordScore += 1;
        }
        if (wordScore === 0 && text.toLowerCase().includes(qWord)) wordScore += 0.5;
        totalScore += wordScore;
    }
    return totalScore;
};

const filterAndSort = (items: Item[], query: string): Item[] => {
    if (!query.trim()) return items;
    return items
        .map(item => ({ item, score: scoreSearch(getDisplayName(item), query) }))
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score)
        .map(({ item }) => item);
};

const parseIdsParam = (param: string | undefined): number[] => {
    if (!param) return [];
    return param.split(',').map(Number).filter(n => !isNaN(n) && n > 0);
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function ProductUrlFilterPopup({ queryParams, onApplyFilter, loading }: Props) {
    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<'url' | 'product' | TextFilterKey>('url');

    const [selectedUrlIds, setSelectedUrlIds] = useState<number[]>([]);
    const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
    const [urlSearch, setUrlSearch] = useState('');
    const [productSearch, setProductSearch] = useState('');
    const [textValues, setTextValues] = useState<Record<TextFilterKey, string>>({
        eventId: '',
        tempEventId: '',
        customerId: '',
        template: '',
    });

    const [products, setProducts] = useState<Item[]>([]);
    const [urlPaths, setUrlPaths] = useState<Item[]>([]);
    const [loadingLists, setLoadingLists] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    // Favorites – initialized from localStorage so they survive reloads & re-logins
    const [urlFavorites, setUrlFavorites] = useState<Item[]>(() => loadFavorites('url'));
    const [productFavorites, setProductFavorites] = useState<Item[]>(() => loadFavorites('product'));
    const [textFavorites, setTextFavorites] = useState<TextValuesMap>(() => loadTextValues('favorites'));
    const [textHistory, setTextHistory] = useState<TextValuesMap>(() => loadTextValues('history'));

    // Record text-filter values that actually returned results, so they show up
    // in the list below the field and can be starred as favorites
    useEffect(() => {
        if (!queryParams || !(+queryParams.total > 0)) return;
        setTextHistory(prev => {
            let next = prev;
            TEXT_FILTERS.forEach(({ key }) => {
                const value = queryParams[key] != null ? String(queryParams[key]).trim() : '';
                if (!value || next[key][0] === value) return;
                next = {
                    ...next,
                    [key]: [value, ...next[key].filter(v => v !== value)].slice(0, TEXT_HISTORY_LIMIT),
                };
            });
            if (next !== prev) persistTextValues('history', next);
            return next;
        });
    }, [queryParams?.eventId, queryParams?.tempEventId, queryParams?.customerId, queryParams?.template, queryParams?.total]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleTextFavorite = (key: TextFilterKey, value: string) => {
        setTextFavorites(prev => {
            const next = {
                ...prev,
                [key]: prev[key].includes(value)
                    ? prev[key].filter(v => v !== value)
                    : [...prev[key], value],
            };
            persistTextValues('favorites', next);
            return next;
        });
    };

    // Whether the current tab list is filtered to show only favorites
    const [showFavUrl, setShowFavUrl] = useState(false);
    const [showFavProduct, setShowFavProduct] = useState(false);

    // ── Load lists once on mount ──────────────────────────────────────────────
    useEffect(() => {
        const loadLists = async () => {
            setLoadingLists(true);
            setLoadError(null);
            try {
                const [productsData, urlPathsData] = await Promise.all([
                    getProductsList(2000),
                    getUrlPathsList(2000),
                ]);
                setProducts(productsData.result || []);
                setUrlPaths(urlPathsData.result || []);
            } catch (e) {
                console.error('Failed to load products/urlpaths', e);
                setLoadError('Failed to load filter data');
            } finally {
                setLoadingLists(false);
            }
        };
        loadLists();
    }, []);

    // ── Sync selection with queryParams when dialog opens ────────────────────
    useEffect(() => {
        if (open) {
            const currentProductIds = parseIdsParam(queryParams?.productIds);
            const currentUrlPathIds = parseIdsParam(queryParams?.urlPathIds);
            setSelectedProductIds(currentProductIds);
            setSelectedUrlIds(currentUrlPathIds);
            setUrlSearch('');
            setProductSearch('');
            setShowFavUrl(false);
            setShowFavProduct(false);
            const currentTextValues: Record<TextFilterKey, string> = {
                eventId: queryParams?.eventId != null ? String(queryParams.eventId) : '',
                tempEventId: queryParams?.tempEventId != null ? String(queryParams.tempEventId) : '',
                customerId: queryParams?.customerId != null ? String(queryParams.customerId) : '',
                template: queryParams?.template != null ? String(queryParams.template) : '',
            };
            setTextValues(currentTextValues);
            const activeTextTab = TEXT_FILTERS.find(f => f.key !== 'template' && currentTextValues[f.key]);
            setActiveTab(
                currentProductIds.length > 0
                    ? 'product'
                    : activeTextTab
                    ? activeTextTab.key
                    : 'url',
            );
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Favorites helpers ─────────────────────────────────────────────────────
    const isFavorite = (id: number, type: 'url' | 'product') =>
        type === 'url'
            ? urlFavorites.some(f => f.id === id)
            : productFavorites.some(f => f.id === id);

    const toggleFavorite = (item: Item, type: 'url' | 'product') => {
        if (type === 'url') {
            const next = urlFavorites.some(f => f.id === item.id)
                ? urlFavorites.filter(f => f.id !== item.id)
                : [...urlFavorites, item];
            setUrlFavorites(next);
            persistFavorites('url', next);
        } else {
            const next = productFavorites.some(f => f.id === item.id)
                ? productFavorites.filter(f => f.id !== item.id)
                : [...productFavorites, item];
            setProductFavorites(next);
            persistFavorites('product', next);
        }
    };

    // ── Filtered / visible lists ──────────────────────────────────────────────
    const filteredUrls = useMemo(
        () => filterAndSort(urlPaths, urlSearch),
        [urlPaths, urlSearch],
    );
    const filteredProducts = useMemo(
        () => filterAndSort(products, productSearch),
        [products, productSearch],
    );

    const visibleUrls = useMemo(() => {
        const base = showFavUrl
            ? filterAndSort(urlFavorites, urlSearch)
            : filteredUrls;
        const selected = base.filter(i => selectedUrlIds.includes(i.id));
        const unselected = base
            .filter(i => !selectedUrlIds.includes(i.id))
            .slice(0, Math.max(0, 100 - selected.length));
        return [...selected, ...unselected];
    }, [filteredUrls, urlFavorites, selectedUrlIds, showFavUrl, urlSearch]);

    const visibleProducts = useMemo(() => {
        const base = showFavProduct
            ? filterAndSort(productFavorites, productSearch)
            : filteredProducts;
        const selected = base.filter(i => selectedProductIds.includes(i.id));
        const unselected = base
            .filter(i => !selectedProductIds.includes(i.id))
            .slice(0, Math.max(0, 100 - selected.length));
        return [...selected, ...unselected];
    }, [filteredProducts, productFavorites, selectedProductIds, showFavProduct, productSearch]);

    // ── Selection toggles ─────────────────────────────────────────────────────
    const toggleItem = (id: number, type: 'url' | 'product') => {
        if (type === 'url') {
            setSelectedUrlIds(prev =>
                prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id],
            );
        } else {
            setSelectedProductIds(prev =>
                prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id],
            );
        }
    };

    // ── Apply / Clear ─────────────────────────────────────────────────────────
    const handleApply = () => {
        // Template shares its query param with the "Mockups" select outside the
        // popup, so it is only overwritten when applied from its own tab
        const filters: AppliedFilters = {
            productIds: null,
            urlPathIds: null,
            eventId: null,
            tempEventId: null,
            customerId: null,
            template: queryParams?.template != null ? String(queryParams.template) : null,
        };
        if (activeTab === 'url') {
            filters.urlPathIds = selectedUrlIds.length > 0 ? selectedUrlIds : null;
        } else if (activeTab === 'product') {
            filters.productIds = selectedProductIds.length > 0 ? selectedProductIds : null;
        } else if (activeTab === 'template') {
            filters.template = textValues.template.trim() || null;
        } else {
            filters[activeTab] = textValues[activeTab].trim() || null;
        }
        onApplyFilter(filters);
        setOpen(false);
    };

    const handleClear = () => {
        setSelectedUrlIds([]);
        setSelectedProductIds([]);
        setTextValues({ eventId: '', tempEventId: '', customerId: '', template: '' });
        onApplyFilter({
            productIds: null,
            urlPathIds: null,
            eventId: null,
            tempEventId: null,
            customerId: null,
            template: null,
        });
        setOpen(false);
    };

    // ── Badge / button state ──────────────────────────────────────────────────
    const activeProductIds = parseIdsParam(queryParams?.productIds);
    const activeUrlPathIds = parseIdsParam(queryParams?.urlPathIds);
    const activeTextCount = TEXT_FILTERS.filter(
        f => f.key !== 'template' && queryParams?.[f.key] != null && String(queryParams[f.key]).trim() !== '',
    ).length;
    const activeFilterCount = activeProductIds.length || activeUrlPathIds.length || activeTextCount;
    const hasActiveFilter = activeFilterCount > 0;

    // ── Render list panel ─────────────────────────────────────────────────────
    const renderList = (
        items: Item[],
        selectedIds: number[],
        type: 'url' | 'product',
        searchValue: string,
        onSearch: (v: string) => void,
        allItems: Item[],
        totalFiltered: number,
        showFav: boolean,
        onToggleShowFav: () => void,
        favorites: Item[],
    ) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', p: 2 }}>

            {/* Search + Favorites toggle row */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                <TextField
                    placeholder={
                        type === 'url'
                            ? 'Search by URL path words…'
                            : 'Search by product name words…'
                    }
                    size="small"
                    fullWidth
                    value={searchValue}
                    autoFocus={false}
                    onChange={e => onSearch(e.target.value)}
                />
                <Tooltip title={showFav ? 'Show all' : 'Select all favorites'}>
                    <Button
                        variant={showFav ? 'contained' : 'outlined'}
                        color="warning"
                        size="small"
                        startIcon={<StarIcon fontSize="small" />}
                        onClick={() => {
                            onToggleShowFav();
                            // Select all favorites of this tab that aren't selected yet
                            if (!showFav && favorites.length > 0) {
                                const favIds = favorites.map(f => f.id);
                                if (type === 'url') {
                                    setSelectedUrlIds(prev => Array.from(new Set([...prev, ...favIds])));
                                } else {
                                    setSelectedProductIds(prev => Array.from(new Set([...prev, ...favIds])));
                                }
                            }
                        }}
                        sx={{ whiteSpace: 'nowrap', flexShrink: 0, height: 40 }}
                    >
                        Favorites
                        {favorites.length > 0 && (
                            <Box
                                component="span"
                                sx={{
                                    ml: 0.75,
                                    bgcolor: 'action.selected',
                                    borderRadius: '10px',
                                    px: 0.75,
                                    fontSize: '0.72rem',
                                    lineHeight: '18px',
                                }}
                            >
                                {favorites.length}
                            </Box>
                        )}
                    </Button>
                </Tooltip>
            </Box>

            {/* Selected chips */}
            {selectedIds.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, maxHeight: 80, overflow: 'auto' }}>
                    {selectedIds.map(id => {
                        const item = allItems.find(i => i.id === id);
                        return item ? (
                            <Chip
                                key={id}
                                label={getDisplayName(item)}
                                size="small"
                                onDelete={() => toggleItem(id, type)}
                                sx={{ maxWidth: 300 }}
                            />
                        ) : null;
                    })}
                </Box>
            )}

            {/* Result count hint */}
            {!showFav && searchValue && totalFiltered > items.length && (
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5 }}>
                    Showing {items.length} of {totalFiltered} results
                </Typography>
            )}
            {showFav && (
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5 }}>
                    {favorites.length === 0
                        ? 'No favorites saved yet'
                        : `${favorites.length} saved favorite${favorites.length !== 1 ? 's' : ''}`}
                </Typography>
            )}

            {/* List */}
            <List
                dense
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 0,
                }}
            >
                {loadingLists ? (
                    <ListItem sx={{ justifyContent: 'center' }}>
                        <CircularProgress size={28} />
                    </ListItem>
                ) : loadError ? (
                    <ListItem>
                        <ListItemText
                            primary={loadError}
                            primaryTypographyProps={{ color: 'error' }}
                        />
                    </ListItem>
                ) : items.length === 0 ? (
                    <ListItem>
                        <ListItemText
                            primary={
                                showFav
                                    ? 'No favorites yet — star items to save them'
                                    : searchValue
                                    ? 'No results found'
                                    : 'No items available'
                            }
                            primaryTypographyProps={{ color: 'textSecondary' }}
                        />
                    </ListItem>
                ) : (
                    items.map(item => {
                        const fav = isFavorite(item.id, type);
                        return (
                            <ListItemButton
                                key={item.id}
                                dense
                                selected={selectedIds.includes(item.id)}
                                onClick={() => toggleItem(item.id, type)}
                                sx={{ py: 0.25, pr: 0.5 }}
                            >
                                <Checkbox
                                    edge="start"
                                    checked={selectedIds.includes(item.id)}
                                    tabIndex={-1}
                                    size="small"
                                    disableRipple
                                />
                                <ListItemText
                                    primary={getDisplayName(item)}
                                    primaryTypographyProps={{
                                        variant: 'body2',
                                        sx: { wordBreak: 'break-all' },
                                    }}
                                />
                                <Tooltip
                                    title={fav ? 'Remove from favorites' : 'Add to favorites'}
                                    placement="left"
                                >
                                    <IconButton
                                        size="small"
                                        onClick={e => {
                                            e.stopPropagation();
                                            toggleFavorite(item, type);
                                        }}
                                        sx={{ flexShrink: 0, color: fav ? 'warning.main' : 'action.disabled' }}
                                    >
                                        {fav ? (
                                            <StarIcon fontSize="small" />
                                        ) : (
                                            <StarBorderIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                </Tooltip>
                            </ListItemButton>
                        );
                    })
                )}
            </List>
        </Box>
    );

    // ── JSX ───────────────────────────────────────────────────────────────────
    return (
        <>
            <Badge badgeContent={activeFilterCount || null} color="primary">
                <Button
                    variant={hasActiveFilter ? 'contained' : 'outlined'}
                    size="small"
                    startIcon={
                        loadingLists
                            ? <CircularProgress size={16} color="inherit" />
                            : <FilterListIcon />
                    }
                    onClick={() => setOpen(true)}
                    disabled={loading}
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    More filters
                </Button>
            </Badge>

            <AppDialog
                open={open}
                onClose={() => setOpen(false)}
                title="More filters"
                description="Filter by URL path, product, an exact id or a template. One filter type at a time: switching tabs replaces the active filter on Apply."
                maxWidth="md"
                actions={(
                    <>
                        <Button onClick={handleClear} color="error" variant="text" sx={{ mr: 'auto' }}>
                            Clear filter
                        </Button>
                        <Button onClick={() => setOpen(false)} variant="outlined">
                            Cancel
                        </Button>
                        <Button onClick={handleApply} variant="contained" color="primary">
                            Apply
                        </Button>
                    </>
                )}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'min(60vh, 540px)',
                        minHeight: 320,
                        overflow: 'hidden',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                    }}
                >
                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => setActiveTab(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{ borderBottom: 1, borderColor: 'divider', px: 1, flexShrink: 0 }}
                    >
                        <Tab
                            value="url"
                            label={
                                selectedUrlIds.length > 0
                                    ? `URL Path (${selectedUrlIds.length} selected)`
                                    : 'URL Path'
                            }
                        />
                        <Tab
                            value="product"
                            label={
                                selectedProductIds.length > 0
                                    ? `Product Name (${selectedProductIds.length} selected)`
                                    : 'Product Name'
                            }
                        />
                        {TEXT_FILTERS.map(f => (
                            <Tab
                                key={f.key}
                                value={f.key}
                                label={textValues[f.key].trim() ? `${f.label} ✓` : f.label}
                            />
                        ))}
                    </Tabs>

                    {activeTab === 'url' &&
                        renderList(
                            visibleUrls,
                            selectedUrlIds,
                            'url',
                            urlSearch,
                            setUrlSearch,
                            urlPaths,
                            filteredUrls.length,
                            showFavUrl,
                            () => setShowFavUrl(v => !v),
                            urlFavorites,
                        )}

                    {activeTab === 'product' &&
                        renderList(
                            visibleProducts,
                            selectedProductIds,
                            'product',
                            productSearch,
                            setProductSearch,
                            products,
                            filteredProducts.length,
                            showFavProduct,
                            () => setShowFavProduct(v => !v),
                            productFavorites,
                        )}

                    {TEXT_FILTERS.map(f => {
                        if (activeTab !== f.key) return null;
                        const favorites = textFavorites[f.key];
                        const savedValues = [
                            ...favorites,
                            ...textHistory[f.key].filter(v => !favorites.includes(v)),
                        ];
                        return (
                            <Box
                                key={f.key}
                                sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', p: 2 }}
                            >
                                <TextField
                                    label={f.label}
                                    placeholder={f.placeholder}
                                    size="small"
                                    fullWidth
                                    value={textValues[f.key]}
                                    onChange={e =>
                                        setTextValues(prev => ({ ...prev, [f.key]: e.target.value }))
                                    }
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') handleApply();
                                    }}
                                />
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, mb: 1 }}>
                                    Exact match. Leave empty and Apply to remove this filter.
                                </Typography>

                                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5 }}>
                                    {savedValues.length > 0
                                        ? 'Previously found values'
                                        : 'Values that return results will appear here'}
                                </Typography>
                                <List
                                    dense
                                    sx={{
                                        flex: 1,
                                        overflow: 'auto',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        p: 0,
                                    }}
                                >
                                    {savedValues.map(value => {
                                        const fav = favorites.includes(value);
                                        return (
                                            <ListItemButton
                                                key={value}
                                                dense
                                                selected={textValues[f.key].trim() === value}
                                                onClick={() =>
                                                    setTextValues(prev => ({ ...prev, [f.key]: value }))
                                                }
                                                sx={{ py: 0.25, pr: 0.5 }}
                                            >
                                                <ListItemText
                                                    primary={value}
                                                    primaryTypographyProps={{
                                                        variant: 'body2',
                                                        sx: { wordBreak: 'break-all' },
                                                    }}
                                                />
                                                <Tooltip
                                                    title={fav ? 'Remove from favorites' : 'Add to favorites'}
                                                    placement="left"
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            toggleTextFavorite(f.key, value);
                                                        }}
                                                        sx={{ flexShrink: 0, color: fav ? 'warning.main' : 'action.disabled' }}
                                                    >
                                                        {fav ? (
                                                            <StarIcon fontSize="small" />
                                                        ) : (
                                                            <StarBorderIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            </Box>
                        );
                    })}
                </Box>
            </AppDialog>
        </>
    );
}
